import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import InstallmentResponse from '../../shared/models/mercado-pago/installment-response';

declare global {
  interface Window {
    MercadoPago: any;
    mp?: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MercadoPagoService {
  private scriptId = 'mercado-pago-sdk';
  private isBrowser: boolean;

  private submitResolver: ((token: string) => void) | null = null;
  private submitRejecter: ((reason?: any) => void) | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  addScript(publicKey: string): void {
    if (!this.isBrowser) return;

    const existingScript = this.document.getElementById(this.scriptId);
    if (existingScript) {
      console.warn('Mercado Pago script já carregado');
      return;
    }

    const script = this.document.createElement('script');
    script.src = 'https://sdk.mercadopago.com/js/v2';
    script.id = this.scriptId;
    script.onload = () => {
      const mp = new window['MercadoPago'](publicKey);
      window['mp'] = mp;
      console.log('Mercado Pago carregado com sucesso');
    };
    this.document.body.appendChild(script);
  }

  removeScript(): void {
    if (!this.isBrowser) return;

    const script = this.document.getElementById(this.scriptId);
    if (script) {
      script.remove();
      console.log('Mercado Pago script removido');
    }

    if (window['mp']) {
      delete window['mp'];
    }
  }

  async getInstallments(params: {
    cardNumber: string;
    transactionAmount: number;
  }): Promise<InstallmentResponse> {
    if (!this.isBrowser || !window['mp'])
      throw new Error('MercadoPago não inicializado');

    const bin = params.cardNumber.replace(/\D/g, '').substring(0, 6);

    try {
      const response: InstallmentResponse[] = await window[
        'mp'
      ].getInstallments({
        amount: Number(params.transactionAmount).toFixed(2),
        bin: bin,
      });

      return response[0] as InstallmentResponse;
    } catch (error) {
      console.error('Erro ao buscar parcelas', error);
      throw error;
    }
  }

  async getCardToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.submitResolver = resolve;
      this.submitRejecter = reject;

      const form = this.document.getElementById(
        'form-checkout'
      ) as HTMLFormElement;
      if (!form) {
        console.error('Formulário do cartão não encontrado');
        return reject('Formulário do cartão não encontrado');
      }
      form.dispatchEvent(
        new Event('submit', { bubbles: true, cancelable: true })
      );
    });
  }

  loadCardFormWithIframe(productCost: number) {
    if (!this.isBrowser || !window['mp']) return;

    const form = {
      id: 'form-checkout',
      cardholderName: {
        id: 'form-checkout__cardholderName',
      },
      cardholderEmail: {
        id: 'form-checkout__cardholderEmail',
      },
      cardNumber: {
        id: 'form-checkout__cardNumber',
      },
      expirationDate: {
        id: 'form-checkout__expirationDate',
      },
      securityCode: {
        id: 'form-checkout__securityCode',
      },
      installments: {
        id: 'form-checkout__installments',
      },
      identificationType: {
        id: 'form-checkout__identificationType',
      },
      identificationNumber: {
        id: 'form-checkout__identificationNumber',
        placeholder: 'Identification number',
      },
      issuer: {
        id: 'form-checkout__issuer',
        placeholder: 'Issuer',
      },
    };

    window['mp'].cardForm({
      amount: Number(productCost).toFixed(2),
      iframe: false,
      form,
      callbacks: {
        onFormMounted: (error: any) => {
          if (error) {
            console.warn('Form Mounted handling error:', error);
          } else {
            console.log('Form mounted');
          }
        },
        onFetching: (resource: string) => {
          console.log('Fetching resource:', resource);
        },
        onCardTokenReceived: (errorData: any, token: any) => {
          if (errorData && errorData.error.fieldErrors.length !== 0) {
            errorData.error.fieldErrors.forEach((errorMessage: string) => {
              console.error(errorMessage);
            });
            this.submitRejecter?.('Erro ao gerar token');
            this.clearResolvers();
          } else {
            this.submitResolver?.(token?.token);
            this.clearResolvers();
          }
        },
      },
    });
  }

  private clearResolvers() {
    this.submitResolver = null;
    this.submitRejecter = null;
  }
}
